/**
 * Sanitize a single-line string by replacing newline characters with spaces.
 * Use this for any field that will be interpolated into AI prompt messages
 * (e.g. email To/Subject, event titles, calendar names) to prevent prompt
 * injection via crafted multi-line values in attacker-controlled content.
 *
 * Covers: CR (\r), LF (\n), U+0085 (NEXT LINE), U+2028 (LINE SEPARATOR),
 * U+2029 (PARAGRAPH SEPARATOR) — all treated as line breaks by JS engines
 * and many parsers.
 *
 * Uses new RegExp() to embed the Unicode codepoints — TypeScript's parser
 * rejects literal U+2028/U+2029 inside regex character class literals.
 */

// U+0085 NEXT LINE, U+2028 LINE SEPARATOR, U+2029 PARAGRAPH SEPARATOR
const LINE_BREAK_RE = new RegExp('[\r\n  ]', 'g');

export function sanitizeLine(str: string): string {
  return str.replace(LINE_BREAK_RE, ' ');
}

// ---------------------------------------------------------------------------
// Email formatting helpers (shared across EmailListCard, EmailThreadCard)
// ---------------------------------------------------------------------------

/** Extract display name from "Name <email@domain>" format. */
export function parseSender(from: string): string {
  const match = from.match(/^([^<]+)</);
  if (match && match[1]) return match[1].trim();
  return from;
}

/** Format a date string as a short time (today) or "Mon D" (other days). */
export function formatEmailTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    if (isToday) {
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/** Format a date string as a full human-readable date+time. */
export function formatFullDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Tool name helpers (shared across ToolCardDispatch, SheetActionCard, DocActionCard, EventConfirmCard)
// ---------------------------------------------------------------------------

/**
 * Strip the MCP transport prefix from a tool name.
 * Claude Code prefixes MCP tools as mcp__<server>__<tool>. The server name can
 * contain underscores (e.g. google_mcp), so use a non-greedy match for the
 * server segment rather than [^_]+ which stops at the first underscore.
 */
export function baseToolName(name: string): string {
  const match = name.match(/^mcp__(.+?)__(.+)$/);
  return match ? match[2] : name;
}

// ---------------------------------------------------------------------------
// Calendar / event time helpers (shared across CalendarListCard, EventConfirmCard)
// ---------------------------------------------------------------------------

/** Extract a plain datetime string from Google Calendar's mixed start/end shape. */
export function getDateTimeStr(val: string | { dateTime?: string; date?: string } | undefined): string {
  if (!val) return "";
  if (typeof val === "string") return val;
  return val.dateTime ?? val.date ?? "";
}

/**
 * Format a Google Calendar start/end value as a short time string.
 * Returns "All day" for date-only ISO values, "HH:MM" otherwise.
 * Returns the raw string (not "") on unparseable input — CalendarListCard
 * version, preferred over the EventConfirmCard variant that returned "".
 */
export function formatEventTime(val: string | { dateTime?: string; date?: string } | undefined): string {
  const str = getDateTimeStr(val);
  if (!str) return "";
  try {
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    if (str.length === 10) return "All day";
    return d.toLocaleTimeString("en-DE", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return str;
  }
}

// ---------------------------------------------------------------------------
// Tool output parsing (shared across all 7 card components)
// ---------------------------------------------------------------------------

export type ToolOutputResult<T> =
  | { kind: "ok"; data: T }
  | { kind: "error"; error: string }
  | { kind: "parse-failed" };

/**
 * Parse and validate a tool output value.
 *
 * Handles three cases:
 *  - Already-parsed object with `ok` field (gateway occasionally returns this)
 *  - JSON string that needs parsing
 *  - Anything that fails to parse or has `ok: false`
 */
export function parseToolOutput<T>(output: unknown): ToolOutputResult<T> {
  // Already-parsed object path
  if (output !== null && typeof output === "object" && "ok" in output) {
    const obj = output as { ok: boolean; error?: string | { reason?: string } };
    if (obj.ok) return { kind: "ok", data: output as T };
    const errMsg =
      typeof obj.error === "string"
        ? obj.error
        : obj.error?.reason ?? "Unknown error";
    return { kind: "error", error: errMsg };
  }

  // String path — need to JSON.parse
  if (typeof output !== "string") return { kind: "parse-failed" };

  try {
    const parsed = JSON.parse(output) as { ok: boolean; error?: string | { reason?: string } };
    if (parsed.ok) return { kind: "ok", data: parsed as T };
    const errMsg =
      typeof parsed.error === "string"
        ? parsed.error
        : (parsed.error as { reason?: string } | undefined)?.reason ?? "Unknown error";
    return { kind: "error", error: errMsg };
  } catch {
    return { kind: "parse-failed" };
  }
}
